import { useEffect, useState } from "react"
import { Loader2 } from "lucide-react"
import type { CampaignSummary } from "../../shared/domain"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { apiErrorMessage } from "@/lib/api"
import { useApp } from "@/lib/app-context"
import { fromDateInput, toDateInput } from "@/lib/format"

/** Criar/editar uma campanha periódica. Destinatários: clientes com email e "recebe inquéritos". */
export function CampaignDialog({
  open,
  campaign,
  onOpenChange,
  onSaved,
}: {
  open: boolean
  campaign: CampaignSummary | null
  onOpenChange(v: boolean): void
  onSaved(): void
}) {
  const { api, toast } = useApp()
  const [name, setName] = useState("")
  const [starts, setStarts] = useState("")
  const [ends, setEnds] = useState("")
  const [reminderDays, setReminderDays] = useState("7")
  const [reminderMax, setReminderMax] = useState("1")
  const [search, setSearch] = useState("")
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open) return
    setName(campaign?.name ?? `Satisfação ${new Date().getFullYear()}`)
    setStarts(toDateInput(campaign?.starts_at))
    setEnds(toDateInput(campaign?.ends_at))
    setReminderDays(campaign ? String(campaign.reminder_after_days ?? "") : "7")
    setReminderMax(String(campaign?.reminder_max ?? 1))
    setSearch(campaign?.search ?? "")
  }, [open, campaign])

  const save = async () => {
    setSaving(true)
    const body = {
      name,
      starts_at: starts ? fromDateInput(starts) : null,
      ends_at: ends ? fromDateInput(ends) : null,
      reminder_after_days: reminderDays ? Number(reminderDays) : null,
      reminder_max: Number(reminderMax) || 0,
      search,
    }
    try {
      if (campaign) await api.updateCampaign(campaign.id, body)
      else await api.createCampaign(body)
      toast("success", campaign ? "Campanha atualizada" : "Campanha criada", campaign ? undefined : "Fica em rascunho até a ativares.")
      onSaved()
    } catch (e) {
      toast("error", "Não foi possível guardar", apiErrorMessage(e))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{campaign ? "Editar campanha" : "Nova campanha"}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="grid gap-1.5">
            <Label>Nome</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label>Início</Label>
              <Input type="date" value={starts} onChange={(e) => setStarts(e.target.value)} />
            </div>
            <div className="grid gap-1.5">
              <Label>Fim</Label>
              <Input type="date" value={ends} onChange={(e) => setEnds(e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label>Lembrete após (dias)</Label>
              <Input type="number" min={1} max={60} value={reminderDays} onChange={(e) => setReminderDays(e.target.value)} placeholder="Sem lembrete" />
            </div>
            <div className="grid gap-1.5">
              <Label>Máximo de lembretes</Label>
              <Input type="number" min={0} max={5} value={reminderMax} onChange={(e) => setReminderMax(e.target.value)} />
            </div>
          </div>
          <div className="grid gap-1.5">
            <Label>Só clientes que contenham (opcional)</Label>
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Nome, NIF, email ou n.º" />
          </div>
          <p className="text-xs text-muted-foreground">
            Recebem os clientes importados com email que não recusaram inquéritos — um por cliente do CRM. O link vale até ao fim da campanha
            (máx. 90 dias). Os envios correm pelo botão “Enviar agora” ou pelo fluxo agendado.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={save} disabled={saving || !name.trim()}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
